import { useState } from 'react'

const API = import.meta.env.VITE_API_URL || '/api'

function HexMark({ size = 30 }) {
  return (
    <svg className="tg-mark" viewBox="0 0 32 32" width={size} height={size} fill="none" aria-hidden="true">
      <defs>
        <clipPath id="hexRP">
          <path d="M16 3 L27 9.5 L27 22.5 L16 29 L5 22.5 L5 9.5 Z" />
        </clipPath>
      </defs>
      <rect x="0" y="0" width="32" height="18.6" fill="currentColor" opacity="0.18" clipPath="url(#hexRP)" />
      <path d="M16 3 L27 9.5 L27 22.5 L16 29 L5 22.5 L5 9.5 Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
      <line x1="6" y1="18.6" x2="26" y2="18.6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  )
}

function PwMeter({ value }) {
  let score = 0
  if (value.length >= 10) score++
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score++
  if (/\d/.test(value)) score++
  if (/[^A-Za-z0-9]/.test(value)) score++
  const colors = ['transparent', 'var(--tg-danger-500)', 'var(--tg-warning-500)', 'var(--tg-warning-500)', 'var(--tg-success-500)']
  const active = value ? Math.max(1, score) : 0
  return (
    <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
      {[1, 2, 3, 4].map(i => (
        <span key={i} style={{ flex: 1, height: 4, borderRadius: 999, background: i <= active ? colors[active] : 'var(--tg-graphite-700)' }} />
      ))}
    </div>
  )
}

export default function ResetPassword() {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token') || ''
  const uid = params.get('uid') || ''

  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  async function handleSubmit(e) {
    e.preventDefault()
    if (password.length < 8) { setErr('Password must be at least 8 characters'); return }
    setLoading(true); setErr('')
    try {
      const res = await fetch(API + '/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: parseInt(uid), token, password }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) { setErr(data.error || ('HTTP ' + res.status)); setLoading(false); return }
      localStorage.setItem('tg_token', data.token)
      localStorage.setItem('tg_user', JSON.stringify(data.user))
      window.location.href = '/'
    } catch (e) { setErr(e.message); setLoading(false) }
  }

  if (!token || !uid) {
    return (
      <div className="auth-wrap" style={{ gridTemplateColumns: '1fr' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <div style={{ width: '100%', maxWidth: 360, textAlign: 'center', padding: 24 }}>
            <h2 style={{ fontSize: 'var(--tg-text-lg)', fontWeight: 'var(--tg-weight-semibold)' }}>Invalid reset link</h2>
            <p style={{ fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-secondary)', margin: '12px 0' }}>
              This reset link is missing or malformed. Request a new one.
            </p>
            <a href="/forgot-password" className="link">Request reset link →</a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-wrap">
      <section className="brand-panel">
        <div className="bp-grid" />
        <div className="bp-brand">
          <HexMark size={30} />
          <span className="wm">Tankguard</span>
        </div>
        <div className="bp-mid">
          <h1>Set a new password</h1>
          <p>Choose something at least 8 characters long. Tankguard recommends a passphrase plus a number and symbol.</p>
        </div>
        <div className="bp-foot">
          <span>© 2026 Tankguard GmbH</span>
          <span>Data hosted in Germany</span>
        </div>
      </section>

      <section className="form-panel">
        <div className="fp-top" />
        <div className="fp-body">
          <div className="fp-inner">
            <h2>New password</h2>
            <p className="sub">After saving you'll be signed in automatically.</p>
            <form className="form" onSubmit={handleSubmit}>
              <div className="tg-field">
                <label className="tg-label" htmlFor="rp-pw">New password</label>
                <input
                  id="rp-pw" className="tg-input" type="password"
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="At least 8 characters" autoComplete="new-password" required autoFocus
                />
                <PwMeter value={password} />
              </div>
              {err && <p className="tg-error">{err}</p>}
              <button
                type="submit"
                className={`tg-btn tg-btn--lg tg-btn--primary btn-full${loading ? ' is-loading' : ''}`}
                disabled={loading || !password}
              >
                <span className="tg-spinner" aria-hidden="true" />
                {loading ? 'Saving…' : 'Save password and sign in'}
              </button>
            </form>
            <div className="switch-line">
              <a href="/" className="link">← Back to sign in</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
